import React from 'react';
import { Globe, Atom, Code2, Lock } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

// The two things a site can be downloaded as, named the way a site owner would
// name them. The Code tab used to offer these as a bare "JSX" label and an
// unlabelled arrow, and the Dashboard as "Download React" / "Download ZIP" —
// three spellings of one choice, none of which said what you would get.
/** @type {{ key: 'html'|'react', icon: typeof Globe, label: string, hint: string }[]} */
export const DOWNLOAD_FORMATS = [
  { key: 'html',  icon: Globe, label: 'HTML, CSS & JS', hint: 'Plain files for any web host' },
  { key: 'react', icon: Atom,  label: 'React project',  hint: 'For developers · Vite' },
];

/**
 * One download menu for every place a site can be downloaded from, so the
 * choices read the same wherever they appear.
 *
 * Users who cannot export still see both options, locked: hiding them hid the
 * fact that downloading is something you can buy. Picking one runs the same
 * handler, which explains the lock and opens pricing. Beneath them sit the two
 * ways to unlock, priced — the same pair the Code tab's locked banner offers.
 * A line of text saying "buy or upgrade" named the fix and made you go and
 * find it.
 *
 * @param {object}   props
 * @param {React.ReactElement} props.children  The trigger button.
 * @param {(format: 'html'|'react') => void} props.onDownload
 * @param {boolean}  props.canExport  Paid plan, or this site was bought.
 * @param {boolean}  [props.hasSite]  False until there is code to download.
 * @param {() => void} [props.onViewCode]  Adds a "View code" item when given.
 * @param {{ sitePrice?: string, planPrice?: string, buying?: boolean,
 *           onBuySite: () => void, onSubscribe: () => void }} [props.unlock]
 *        Prices arrive formatted in the visitor's currency, which the server
 *        picks from their IP; blank until /billing/pricing answers, and the
 *        buttons still work without them.
 * @param {'start'|'center'|'end'} [props.align]  Menu edge aligned to the trigger.
 */
export default function DownloadMenu({ children, onDownload, canExport, hasSite = true, onViewCode, unlock, align = 'end' }) {
  const itemClass = 'cursor-pointer gap-2.5 px-2 py-2 text-zinc-200 focus:bg-zinc-800 focus:text-white';
  const unlockClass = 'cursor-pointer flex-col items-stretch gap-0 rounded-lg px-3 py-2 text-center';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-60 bg-zinc-950 border-zinc-800 text-zinc-200">
        {DOWNLOAD_FORMATS.map(({ key, icon: Icon, label, hint }) => (
          <DropdownMenuItem
            key={key}
            disabled={!hasSite}
            onSelect={() => onDownload(key)}
            className={`${itemClass} items-start`}
          >
            <Icon className="mt-0.5 text-orange-400" />
            <span className="flex-1 min-w-0">
              <span className="block text-[13px] font-medium leading-snug">{label}</span>
              <span className="block text-[11px] text-zinc-500 leading-snug">{hint}</span>
            </span>
            {!canExport && (
              <>
                <Lock className="mt-0.5 !size-3.5 text-zinc-500" aria-hidden="true" />
                <span className="sr-only">(locked)</span>
              </>
            )}
          </DropdownMenuItem>
        ))}

        {!hasSite ? (
          <p className="px-2 pt-1 pb-1.5 text-[11px] text-zinc-500 leading-relaxed">
            Available once your site is built.
          </p>
        ) : !canExport && unlock && (
          <div className="px-1 pt-1.5 pb-1 space-y-1.5">
            {/* Stays open on select: checkout is a redirect that takes a
                moment, and "Opening checkout…" is the only sign it is coming. */}
            <DropdownMenuItem
              disabled={unlock.buying}
              onSelect={(e) => { e.preventDefault(); unlock.onBuySite(); }}
              className={`${unlockClass} bg-orange-500 text-ink focus:bg-orange-400 focus:text-ink`}
            >
              <span className="text-[12px] font-semibold leading-tight">
                {unlock.buying ? 'Opening checkout…' : `Buy this site${unlock.sitePrice ? ` — ${unlock.sitePrice}` : ''}`}
              </span>
              <span className="text-[10px] text-orange-50/80 leading-tight mt-0.5">One payment · this site only</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={unlock.onSubscribe}
              className={`${unlockClass} border border-zinc-700 bg-zinc-900 text-zinc-100 focus:bg-zinc-800 focus:text-white`}
            >
              <span className="text-[12px] font-semibold leading-tight">
                {unlock.planPrice ? `Subscribe — ${unlock.planPrice}/month` : 'Subscribe monthly'}
              </span>
              <span className="text-[10px] text-zinc-500 leading-tight mt-0.5">More access · every site you build</span>
            </DropdownMenuItem>
          </div>
        )}

        {onViewCode && (
          <>
            <DropdownMenuSeparator className="bg-zinc-800" />
            <DropdownMenuItem onSelect={onViewCode} className={`${itemClass} items-center`}>
              <Code2 className="text-zinc-400" />
              <span className="text-[13px]">View code</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
